import { action, observable, runInAction } from 'mobx'
import { createContext } from 'react'

// eslint-disable-next-line import/no-cycle
import { getGlobalSettings } from '../services/globalSettings.service'

import { BaseStore } from './BaseStore'

const INITIAL_STATE = {
  isGlobalRoListEnabled: false,
  isRoTimelineEnabled: false,
  isAnalyticsEnabled: false,
  loaded: false,
}

const TYPES = {
  isGlobalRoListEnabled: observable,
  isRoTimelineEnabled: observable,
  isAnalyticsEnabled: observable,
  loaded: observable,
}

/**
 * Backend feature flags (GET /global-settings/), resolved per-user.
 *
 * MobX-observable so `observer` consumers (e.g. the nav bar) re-render when the
 * flags arrive — a plain React-context value does not, because `observer`'s
 * shouldComponentUpdate skips context-only changes. The load is triggered once
 * the user is authenticated and loaded, wired via a reaction in RootStore.
 */
export class GlobalSettingsStore extends BaseStore {
  // Tracks the in-flight request so a newer load() — or a logout/reset — can
  // abort it. Non-observable: it's plumbing, not UI state.
  _controller = null

  load = action(async () => {
    // Supersede any in-flight load (e.g. a rapid re-auth) so only the latest wins.
    this._controller?.abort()
    const controller = new AbortController()
    this._controller = controller

    const data = await getGlobalSettings(undefined, undefined, controller.signal)

    // Aborted by a newer load or by logout/reset: drop the response entirely so
    // a late resolve can't repopulate flags (or re-set loaded) after reset.
    if (controller.signal.aborted) return

    runInAction(() => {
      if (data) {
        this.isGlobalRoListEnabled = !!data.isGlobalRoListEnabled
        this.isRoTimelineEnabled = !!data.isRoTimelineEnabled
        this.isAnalyticsEnabled = !!data.isAnalyticsEnabled
      }
      this.loaded = true
    })
  })

  // Override BaseStore.reset to also abort any in-flight load before clearing
  // state, so a request still on the wire at logout can't overwrite the reset.
  reset = action(() => {
    this._controller?.abort()
    this._controller = null
    this.isGlobalRoListEnabled = INITIAL_STATE.isGlobalRoListEnabled
    this.isRoTimelineEnabled = INITIAL_STATE.isRoTimelineEnabled
    this.isAnalyticsEnabled = INITIAL_STATE.isAnalyticsEnabled
    this.loaded = INITIAL_STATE.loaded
  })
}

export const globalSettings = new GlobalSettingsStore(INITIAL_STATE, TYPES)
export const GlobalSettingsContext = createContext(globalSettings)
